import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toastBus, type ToastPayload, type ToastType } from './toastBus';

type ToastItem = {
    id: string;
    type: ToastType;
    title?: string;
    message: string;
    duration: number;
};

type ToastContextValue = {
    show: (payload: ToastPayload) => void;
    success: (message: string, opts?: Omit<ToastPayload, 'message' | 'type'>) => void;
    error: (message: string, opts?: Omit<ToastPayload, 'message' | 'type'>) => void;
    info: (message: string, opts?: Omit<ToastPayload, 'message' | 'type'>) => void;
    warning: (message: string, opts?: Omit<ToastPayload, 'message' | 'type'>) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

function makeId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function toneByType(type: ToastType) {
    switch (type) {
        case 'success':
            return {
                wrap: 'border-emerald-200 bg-emerald-50',
                icon: 'bg-emerald-500',
                title: 'text-emerald-800',
                body: 'text-emerald-700',
            };
        case 'error':
            return {
                wrap: 'border-rose-200 bg-rose-50',
                icon: 'bg-rose-500',
                title: 'text-rose-800',
                body: 'text-rose-700',
            };
        case 'warning':
            return {
                wrap: 'border-amber-200 bg-amber-50',
                icon: 'bg-amber-500',
                title: 'text-amber-800',
                body: 'text-amber-700',
            };
        default:
            return {
                wrap: 'border-slate-200 bg-white',
                icon: 'bg-sky-500',
                title: 'text-slate-800',
                body: 'text-slate-600',
            };
    }
}

function ToastCard({ item, onClose }: { item: ToastItem; onClose: (id: string) => void }) {
    const tone = toneByType(item.type);

    return (
        <div
            className={[
                'pointer-events-auto w-[360px] max-w-[calc(100vw-32px)] rounded-2xl border shadow-lg',
                'px-4 py-3 backdrop-blur-sm',
                tone.wrap,
            ].join(' ')}
        >
            <div className="flex items-start gap-3">
                <span className={['mt-1 h-2.5 w-2.5 rounded-full shrink-0', tone.icon].join(' ')} />
                <div className="min-w-0 flex-1">
                    {item.title ? (
                        <div className={['text-sm font-semibold', tone.title].join(' ')}>{item.title}</div>
                    ) : null}
                    <div className={['text-sm leading-5 whitespace-pre-wrap break-words', tone.body].join(' ')}>
                        {item.message}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={() => onClose(item.id)}
                    className="shrink-0 rounded-md px-1 text-slate-400 hover:bg-black/5 hover:text-slate-600"
                    aria-label="닫기"
                >
                    ×
                </button>
            </div>
        </div>
    );
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
    const [items, setItems] = useState<ToastItem[]>([]);

    const remove = useCallback((id: string) => {
        setItems((prev) => prev.filter((item) => item.id !== id));
    }, []);

    const show = useCallback((payload: ToastPayload) => {
        const id = payload.id ?? makeId();
        const item: ToastItem = {
            id,
            type: payload.type ?? 'info',
            title: payload.title,
            message: payload.message,
            duration: payload.duration ?? 3200,
        };

        setItems((prev) => [...prev, item]);
    }, []);

    useEffect(() => {
        return toastBus.subscribe((payload) => {
            show(payload);
        });
    }, [show]);

    useEffect(() => {
        if (items.length === 0) return;

        const timers = items.map((item) =>
            window.setTimeout(() => {
                remove(item.id);
            }, item.duration),
        );

        return () => {
            timers.forEach((t) => window.clearTimeout(t));
        };
    }, [items, remove]);

    const value = useMemo<ToastContextValue>(
        () => ({
            show,
            success: (message, opts) => show({ type: 'success', message, ...opts }),
            error: (message, opts) => show({ type: 'error', message, ...opts }),
            info: (message, opts) => show({ type: 'info', message, ...opts }),
            warning: (message, opts) => show({ type: 'warning', message, ...opts }),
        }),
        [show],
    );

    return (
        <ToastContext.Provider value={value}>
            {children}

            <div className="pointer-events-none fixed right-4 top-4 z-[100] flex flex-col gap-2">
                {items.map((item) => (
                    <ToastCard key={item.id} item={item} onClose={remove} />
                ))}
            </div>
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return ctx;
}
